import { useCallback } from 'react';
import { useReport } from '../../ReportContext';
import { useItemNavigation } from '../../hooks/useItemNavigation';

// Shared drilldown handler
export const useDrilldownHandler = (config) => {
  const reportState = useReport();
  const { handleDrilldown, isDrilldownDisabled } = reportState;
  const { handleItemClick } = useItemNavigation(config, reportState);
  
  return useCallback((chartItem, payload, seriesData) => {
    // If drilldown is disabled for the current level, do nothing
    if (isDrilldownDisabled) {
      return;
    }
    
    // Make sure we capture the ID from the clicked item
    const itemId = chartItem.id || (payload && payload.id);
    
    const drilldownData = {
      ...chartItem,
      ...(payload || {}),
      ...seriesData,
      id: itemId, // Ensure ID is properly set
      value: chartItem[seriesData?.key] || chartItem.count || chartItem.value,
      seriesKey: seriesData?.key
    };
    
    // For Zone items, explicitly set modelVersionZoneId to ensure it's captured
    if (drilldownData.depth === 'Z') {
      drilldownData.modelVersionZoneId = itemId;
    }
    
    // First, try to handle with item navigation if we're at bottom level
    // This allows clicking items to navigate to data views
    const handled = handleItemClick(drilldownData);
    if (handled) {
      return; // If navigation was handled, don't proceed with drilldown
    }
    
    // Otherwise proceed with regular drilldown if available
    if (!config?.drilldown || seriesData?.drilldown === false) {
      return;
    }
    
    // Block drilldown for pause time data - check several possible conditions
    const isPauseTime = 
      (chartItem && (
        chartItem.id === 'pause' ||
        chartItem._isPauseData ||
        chartItem.name === 'Pause Time'
      )) ||
      (payload && (
        payload.id === 'pause' || 
        payload._isPauseData || 
        payload.name === 'Pause Time'
      )) ||
      (seriesData && seriesData.key === 'pauseMinutes');
      
    if (isPauseTime) {
      return;
    }
    
    // Proceed with regular drilldown
    handleDrilldown(drilldownData);
  }, [config, handleDrilldown, isDrilldownDisabled, handleItemClick]);
}; 